import * as S from './styles';
import { colors } from '@/constants';
import { useCalcPrice, useComboCartPrice } from '@/talons';
import { CartItems } from '@/hooks/CartContext';
import { useClipboard } from '@/shared/clipboard';
import { RiExchange2Line, RiInformationLine } from 'react-icons/ri';
import { RiCalendarTodoLine } from 'react-icons/ri';
import { AddToCart, Packaging, StPrice, Stock } from '@/components';
import { formatCurrency, getEffectivePriceRecordForPack, isNroSegmentoPrecoCuringa } from '@/shared';
import { useMemo, useState, useEffect, useLayoutEffect, memo, useCallback, useRef } from 'react';
import { isAfter, isBefore, isValid, parse, startOfDay } from 'date-fns';
import {
  useCart,
  useClient,
  useDiscountFlex,
  useGlobals,
  useModalAttributes,
  useModalFlex,
  usePricingValidationClient,
  useSelectedClientScopeWithFallback,
  useStock,
  useTables,
} from '@/hooks';
import { CardDescription, CardMode, ClientProps, Combo, PackagesTypesProps, PackagingProductProps, PriceProductProps, ProductProps, TaxSubstitutionProps } from '@/types';
import { ProductImage } from '@/components';
import { ComboProduct } from './ComboProduct';
import { useComboCartItems } from '@/talons/combos/useComboCartItems';
import {
  collectFlexDiscountLookupKeys,
  isActiveComboPortionFromAllocations,
  pickLatestFlexRecord,
  shouldMigrateFlexToScopeKey,
} from '@/talons/combos/comboFlexEligibility';

interface AddProductProps {
  sku: number;
  quantity: number;
  option_uid: string;
  uid: string;
  category_id: number;
}

interface UpdateProductAmountProps {
  sku: number;
  uid: string;
  quantity: number;
}

interface Product {
  showTime?: boolean;
  product: ProductProps;
  showInfos?: boolean;
  showFlex?: boolean;
  direction?: CardMode;
  paymentCod?: number | string;
  removeProductFromCart?: ({ uid }: { uid: string }) => void;
  updateProductAmount?: ({ sku, uid, quantity }: UpdateProductAmountProps) => void;
  addProductToCart?: ({ sku, quantity, option_uid, uid, category_id }: AddProductProps) => void;
  handleModalFlex?: (product: ProductProps, pack: PackagesTypesProps, packQty: number) => void;
  onProductNavigate?: () => void;
  // Preço de combo (riscado + valor promocional) só aparece no carrinho e no card de produto usado
  // no picker de combo (ComboGroupItem). Nos demais contextos (listagem, categoria, busca) o card
  // sempre mostra o preço original, mesmo que a linha já esteja com combo ativo no carrinho.
  showComboPricing?: boolean;
}

interface PropsFlex extends Product {
  substTrib: TaxSubstitutionProps[];
}

interface PropsNoFlex extends Product {
  substTrib?: never;
}

export type Props = PropsFlex | PropsNoFlex;

// Nome + descrição
const ProductInfo = memo(
  ({
    product,
    onProductNavigate,
  }: {
    product: ProductProps;
    ean: string | undefined;
    commissionFlag: string | null;
    cardDescription: CardDescription;
    onProductNavigate?: () => void;
  }) => {
    return (
      <S.Div>
        <S.ProductLink to={`/${product.COD_PRODUTO}`} onClick={onProductNavigate}>
          <S.DescProduct>
            {product.DESC_PRODUTO_BUSCA || product.DESC_PRODUTO}
          </S.DescProduct>
        </S.ProductLink>
      </S.Div>
    );
  }
);

// Última compra
const LastPurchase = memo(
  ({ lastPurchase, direction, label = 'itens' }: { lastPurchase: any; direction: CardMode; label: 'itens' | 'unidades' }) =>
    lastPurchase && (
      <S.LastPurchaseBox $grid={direction === 'grid'}>
        <S.LastPurchaseRow>
          <RiCalendarTodoLine size={16} />
          {direction === 'list' && <S.LastPurchaseText>Última venda:</S.LastPurchaseText>}
          <S.Space />
          <S.LastPurchaseText>{lastPurchase?.date}</S.LastPurchaseText>
        </S.LastPurchaseRow>
        <S.LastPurchaseRow>
          <S.LastPurchaseBold>
            {lastPurchase?.quantity}{' '}
            {lastPurchase?.quantity > 1 ? (label === 'itens' ? 'itens' : 'unidades') : label === 'itens' ? 'item' : 'unidade'}
          </S.LastPurchaseBold>
          <S.LastPurchaseBold>{formatCurrency({ value: lastPurchase?.value })}</S.LastPurchaseBold>
        </S.LastPurchaseRow>
      </S.LastPurchaseBox>
    )
);

// Preço + Flex + Estoque
const PriceContent = memo(
  ({
    product,
    hasStock,
    price,
    unityPrice,
    discountPrice,
    packQty,
    effectiveFinalPrice,
    selectedClient,
    showFlex,
    showInfosFlex,
    rawQuantity,
    packQuantity,
    direction,
    handleOpenFlexModal,
    flexState,
    flexPercentual,
    hasComboDiscount,
    originalUnitPrice,
    originalPackPrice,
    priceRecordForSt,
  }: {
    product: ProductProps;
    showOldPrice: boolean;
    hasStock: boolean;
    price: PriceProductProps;
    unityPrice: number;
    discountPrice: number;
    packQty: number;
    effectiveFinalPrice: number;
    effectiveRecord: PriceProductProps | null;
    selectedClient: ClientProps;
    uid: string;
    discounts: any;
    showFlex: boolean;
    showInfosFlex: boolean;
    rawQuantity: number;
    packQuantity: number;
    direction: CardMode;
    handleOpenFlexModal: () => void;
    flexState?: 'positive' | 'negative';
    flexPercentual?: number;
    hasComboDiscount?: boolean;
    originalUnitPrice?: number;
    originalPackPrice?: number;
    priceRecordForSt?: PriceProductProps | null;
  }) => {
    const stRecord = priceRecordForSt ?? price;

    return (
    <S.PriceContent $isList={direction === 'list'} $bordered={hasStock && effectiveFinalPrice > 0}  hasComboDiscount={hasComboDiscount}>
      <S.RowPrice>
        {packQty && effectiveFinalPrice && hasStock && (
          <>
            <S.ColPrice>
              {hasComboDiscount && originalUnitPrice != null && (
                <S.Underline>
                  {formatCurrency({ value: originalUnitPrice })}/un
                </S.Underline>
              )}
              <S.UnitPrice $combo={hasComboDiscount}>
                {formatCurrency({
                  value: unityPrice,
                })}
                /un
              </S.UnitPrice>
              {hasComboDiscount && originalPackPrice != null && (
                <S.Underline>
                  {formatCurrency({ value: originalPackPrice })}
                </S.Underline>
              )}
              <S.Price $combo={hasComboDiscount}>
                {formatCurrency({
                  value: effectiveFinalPrice,
                })}
              </S.Price>

              {price?.DESTACA_ST === 'S' && selectedClient?.COD_LOJA === 743 && stRecord?.VLR_PRECO_CALCULADO && (
                <StPrice
                  precoSt={stRecord?.ST_VLR_FINAL || stRecord?.ST_VLR_FINAL_CART}
                  precoCalculado={stRecord?.VLR_PRECO_CALCULADO}
                  cliente={selectedClient}
                  codTributacaoProduto={stRecord?.COD_TRIBUTACAO_PRODUTO}
                  precoPauta={stRecord?.VLR_PAUTA}
                  descontoFlex={hasComboDiscount ? 0 : flexPercentual || 0}
                />
              )}

              <Stock stockLeft={hasStock ? Math.floor(rawQuantity / packQuantity) : 0} extraMarginTop={false} />
            </S.ColPrice>
          </>
        )}
      </S.RowPrice>

      {/* Preserva o espaço do botão Flex mesmo quando não exibido */}
      {showFlex && showInfosFlex && hasStock && effectiveFinalPrice > 0 ? (
        <S.ButtonFlex
          $isdisabled={!(discountPrice || effectiveFinalPrice) || !hasStock}
          disabled={!(discountPrice || effectiveFinalPrice) || !hasStock}
          data-test={`button-flex-${product.COD_PRODUTO}`}
          onClick={handleOpenFlexModal}
          $isList={direction === 'list'}
          $flexState={flexState}
        >
          <S.EditIcon size={20} />
          {flexPercentual !== 0 ? `${flexPercentual?.toFixed(2)}%` : 'Flex'}
        </S.ButtonFlex>
      ) : (
        // Placeholder vazio: mantém o espaço sem exibir conteúdo visível
        <S.ButtonFlex
          $isdisabled={true}
          disabled
          aria-hidden="true"
          data-test={`button-flex-${product.COD_PRODUTO}-placeholder`}
          onClick={() => { }}
          $isList={direction === 'list'}
          $flexState={undefined}
          style={{
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        />
      )}
    </S.PriceContent>
    );
  }
);

const RenderProducts = ({
  product,
  substTrib,
  paymentCod,
  showFlex = true,
  showInfos = true,
  direction = 'grid',
  onProductNavigate,
  showComboPricing = false,
}: Props) => {
  const { stockLeft } = useStock();
  const { getPrices } = useCalcPrice();
  const { selectedClient } = useClient();
  const { copyToClipboard } = useClipboard();
  const { openModal: openModalFlex } = useModalFlex();
  const { paymentCode, cardDescription } = useGlobals();
  const { getLimit, currentCartDetails, getStockMsg } = useCart();
  const { getDiscountProduct, setDiscount, deleteDiscount, discounts } = useDiscountFlex();
  const { getComboCartItemInfo } = useComboCartItems();
  const { openModal: openModalAttributes } = useModalAttributes();

  const isChangedPack = useRef(false);

  const modePayment = useMemo(() => Number(paymentCode || paymentCod), [paymentCode, paymentCod]);

  const getEmbalagemQty = useCallback((emb?: PackagingProductProps) => {
    if (!emb) return 0;
    return emb.SG_EMBALAGEM === 'PK' ? emb.QTD_MULTIPLO_VENDA : emb.QTD_EMBALAGEM;
  }, []);

  const [pack, setPackage] = useState(product.EMBALAGEM?.[0]?.SG_EMBALAGEM);
  const [packQuantity, setPackQuantity] = useState(getEmbalagemQty(product?.EMBALAGEM?.[0]) ?? 0);
  const toNumber = useCallback((value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);
  const selectedClientScope = useSelectedClientScopeWithFallback();
  const {
    pricingClient: selectedClientForPackPricing,
    catalogScopeKey,
    clientLojaForPrice,
    clientSegsForPrice,
  } = usePricingValidationClient();

  useLayoutEffect(() => {
    const list = product?.EMBALAGEM;
    if (!list?.length) return;

    const stillValid = list.some((e) => {
      if (pack === 'PK') {
        return e.SG_EMBALAGEM === 'PK' && Number(packQuantity) === Number(e.QTD_MULTIPLO_VENDA);
      }
      return e.SG_EMBALAGEM === pack && Number(packQuantity) === Number(e.QTD_EMBALAGEM);
    });

    if (stillValid) return;

    const first = list[0];
    setPackage(first.SG_EMBALAGEM as PackagesTypesProps);
    setPackQuantity(getEmbalagemQty(first) ?? 0);
  }, [catalogScopeKey, product?.COD_PRODUTO, product?.EMBALAGEM, pack, packQuantity, getEmbalagemQty]);

  const setPackageIntercept = useCallback((val: PackagesTypesProps) => {
    isChangedPack.current = true; // marca mudança manual
    setPackage(val);
  }, []);

  const setPackQuantityIntercept = useCallback((val: number) => {
    isChangedPack.current = true; // marca mudança manual
    setPackQuantity(val);
  }, []);

  const packing = useMemo(
    () =>
      product.EMBALAGEM.find((p) => {
        if (pack === 'PK') {
          return pack === p?.SG_EMBALAGEM && packQuantity === p?.QTD_MULTIPLO_VENDA;
        }
        return pack === p?.SG_EMBALAGEM && packQuantity === p?.QTD_EMBALAGEM;
      }) as PackagingProductProps,
    [product, pack, packQuantity]
  );

  // Linha real do carrinho para este produto+embalagem (se já adicionado) — usada para saber se a
  // quantidade já em carrinho está com combo ativo. Precisa vir antes de discountPrice/preço final:
  // com combo ativo, o preço de combo tem prioridade sobre catálogo/flex, igual ao carrinho (ver
  // comboFlexEligibility / useComboCartPrice).
  const cartItemForPacking = useMemo(() => {
    const items = currentCartDetails?.items;
    if (!items?.length) return undefined;

    return items.find((i) => {
      const [emb, qtd] = i?.option_uid?.split('_') as [PackagesTypesProps, string];

      if (packing?.QTD_MULTIPLO_VENDA > 1 && emb === 'PK') {
        return (
          i?.sku === product?.COD_PRODUTO && packing?.SG_EMBALAGEM === emb && qtd === packing?.QTD_MULTIPLO_VENDA?.toString()
        );
      }

      return i?.sku === product?.COD_PRODUTO && packing?.SG_EMBALAGEM === emb && qtd === packing?.QTD_EMBALAGEM?.toString();
    });
  }, [
    currentCartDetails,
    packing?.QTD_MULTIPLO_VENDA,
    packing?.SG_EMBALAGEM,
    packing?.QTD_EMBALAGEM,
    product?.COD_PRODUTO,
  ]);

  const comboAllocationsForLine = useMemo(
    () => (cartItemForPacking ? getComboCartItemInfo(cartItemForPacking.uid) : undefined),
    [cartItemForPacking, getComboCartItemInfo]
  );

  const isComboActiveForLine = useMemo(() => {
    if (!cartItemForPacking) return false;

    return isActiveComboPortionFromAllocations(cartItemForPacking, comboAllocationsForLine);
  }, [cartItemForPacking, comboAllocationsForLine]);

  // Preço/ST de combo só neste card quando `showComboPricing` está ligado (picker da tela de
  // combos). Listagem, categoria e busca nunca sobrepõem o original — combo fica no carrinho e
  // na tela de combos.
  const applyComboPricing = showComboPricing && isComboActiveForLine;

  // Preço de combo para esta linha, calculado do mesmo jeito que o carrinho (useComboCartPrice) —
  // sem isso o card nunca sabe o valor promocional do combo nem quando ele muda com a quantidade.
  const comboCartPrice = useComboCartPrice({
    cartItemUid: cartItemForPacking?.uid ?? '',
    product,
    option_uid: `${pack}_${packQuantity}`,
    paymentCondition: modePayment,
    enabled: showComboPricing && Boolean(cartItemForPacking),
    applyComboDiscount: applyComboPricing,
  });

  const productPrices = useMemo(() => getPrices(product), [getPrices, product, modePayment, direction, catalogScopeKey]);

  const price = useMemo(
    () =>
      (productPrices.find((p) => {
        const segmentCode = toNumber(p?.NRO_SEGMENTO);
        const segs =
          segmentCode == null ||
          isNroSegmentoPrecoCuringa(segmentCode) ||
          !clientSegsForPrice.length ||
          clientSegsForPrice.some((s) => s === segmentCode);
        const clientLoja = clientLojaForPrice;
        const lojaPreco = toNumber(p?.COD_LOJA);
        const lojaOk = clientLoja == null || lojaPreco == null || lojaPreco === clientLoja;
        if (pack === 'PK') {
          return (
            p.SG_EMBALAGEM === pack &&
            packQuantity === p.QTD_MULTIPLO_VENDA &&
            p.NROCONDICAOPAGTO === modePayment &&
            lojaOk &&
            segs
          );
        }
        return (
          p.SG_EMBALAGEM === pack &&
          p.QTD_EMBALAGEM === packQuantity &&
          p.NROCONDICAOPAGTO === modePayment &&
          lojaOk &&
          segs
        );
      }) as PriceProductProps) || ({} as PriceProductProps),
    [productPrices, pack, packQuantity, modePayment, clientLojaForPrice, clientSegsForPrice, toNumber]
  );

  // Precisa bater com o uid gerado por ContentModalFlex e AddToCart para o mesmo produto/embalagem.
  const flexUid = useMemo(
    () => `${product.COD_PRODUTO}-${pack}-${packQuantity}`,
    [product.COD_PRODUTO, pack, packQuantity]
  );

  const flexLookupKeys = useMemo(
    () => collectFlexDiscountLookupKeys(cartItemForPacking, comboAllocationsForLine, flexUid),
    [cartItemForPacking, comboAllocationsForLine, flexUid]
  );

  const flexEmbQty = useMemo(() => {
    const emb = product.EMBALAGEM.find((i) => {
      if (pack === 'PK') {
        return i.SG_EMBALAGEM === pack && packQuantity === i?.QTD_MULTIPLO_VENDA;
      }
      return i.SG_EMBALAGEM === pack && packQuantity === i?.QTD_EMBALAGEM;
    });
    return getEmbalagemQty(emb);
  }, [pack, packQuantity, product.EMBALAGEM, getEmbalagemQty]);

  const { record: discountRecord, uid: flexWriteUid } = useMemo(() => {
    return pickLatestFlexRecord(flexLookupKeys, (uid) =>
      getDiscountProduct({
        package: pack,
        productId: product.COD_PRODUTO,
        quantity: flexEmbQty,
        uid,
      })
    );
  }, [flexLookupKeys, getDiscountProduct, pack, product.COD_PRODUTO, flexEmbQty]);

  const discountScopeKey = flexWriteUid;

  useEffect(() => {
    const scopedKey = flexLookupKeys[0];
    if (!scopedKey) return;

    const lookup = (uid: string) =>
      getDiscountProduct({
        package: pack,
        productId: product.COD_PRODUTO,
        quantity: flexEmbQty,
        uid,
      });

    const simple = lookup(flexUid);
    const scoped = lookup(scopedKey);
    if (!shouldMigrateFlexToScopeKey(flexUid, scopedKey, simple, scoped) || !simple) return;

    setDiscount({
      package: pack,
      quantity: flexEmbQty,
      productId: product.COD_PRODUTO,
      uid: scopedKey,
      percentual: simple.percentual ?? 0,
      price: simple.discountPrice || {},
      saldoAFaturar: simple.saldoAFaturar || 0,
    });
    deleteDiscount({
      package: pack,
      quantity: flexEmbQty,
      productId: product.COD_PRODUTO,
      uid: flexUid,
    });
  }, [
    flexLookupKeys,
    flexUid,
    getDiscountProduct,
    pack,
    product.COD_PRODUTO,
    flexEmbQty,
    setDiscount,
    deleteDiscount,
    discounts,
  ]);

  const discountPrice = useMemo(() => {
    // Combo ATIVO não acumula flex (ver comboFlexEligibility): o desconto de combo vale sozinho.
    // O flex salvo continua no contexto — só deixa de ser lido enquanto a linha estiver com combo ativo
    // E o card estiver no contexto que exibe preço de combo (ver showComboPricing).
    if (applyComboPricing) return undefined;

    return discountRecord?.discountPrice?.[modePayment];
  }, [applyComboPricing, discountRecord, modePayment]);

  const flexPercentual = useMemo(() => {
    return typeof discountRecord?.percentual === 'number' ? discountRecord.percentual : 0;
  }, [discountRecord]);

  const flexState = useMemo<undefined | 'positive' | 'negative'>(() => {
    if (flexPercentual > 0) return 'positive';
    if (flexPercentual < 0) return 'negative';
    return undefined;
  }, [flexPercentual]);

  const commissionFlag = useMemo(() => {
    if (!product || !pack) return null;

    const foundItem = product.EMBALAGEM.find((p) => {
      if (pack === 'PK') {
        return p.SG_EMBALAGEM === pack && p.QTD_MULTIPLO_VENDA === packQuantity;
      }
      return p.SG_EMBALAGEM === pack && p.QTD_EMBALAGEM === packQuantity;
    });

    return foundItem ? foundItem.SG_COMISSAO : null;
  }, [product, pack, packQuantity]);

  const ean = useMemo(() => {
    // busca ean na embalagem
    const eanEmb = product.EMBALAGEM?.find(emb => emb?.EAN_PRODUTO)?.EAN_PRODUTO;
    if (eanEmb) return String(eanEmb);

    // se não encontrar na embalagem, busca no atributo
    const eanAttr = product.ATRIBUTOS.find((at) => at.ean_grp)?.ean_grp
    return eanAttr;
  }, [product]);

  const sku = useMemo(() => product.COD_PRODUTO, [product]);

  const quantity = useMemo(() => {
    const items = currentCartDetails?.items;
    if (!items?.length) return 0;

    return (
      items.find((i) => {
        const [emb, qtd] = i?.option_uid?.split('_') as [PackagesTypesProps, string];

        if (packing?.QTD_MULTIPLO_VENDA > 1 && emb === 'PK') {
          return (
            i?.sku === product?.COD_PRODUTO && packing?.SG_EMBALAGEM === emb && qtd === packing?.QTD_MULTIPLO_VENDA?.toString()
          );
        }

        return i?.sku === product?.COD_PRODUTO && packing?.SG_EMBALAGEM === emb && qtd === packing?.QTD_EMBALAGEM?.toString();
      })?.quantity || 0
    );
  }, [
    currentCartDetails,
    packing?.QTD_MULTIPLO_VENDA,
    packing?.SG_EMBALAGEM,
    packing?.QTD_EMBALAGEM,
    product?.COD_PRODUTO,
  ]);

  const hasMultiplePacking = useMemo(
    () =>
      product?.EMBALAGEM?.length > 1 &&
        currentCartDetails?.items?.find((i: CartItems) => i?.sku === product?.COD_PRODUTO)
        ? currentCartDetails?.items?.find((i: CartItems) => i?.sku === product?.COD_PRODUTO)
        : null,
    [product, currentCartDetails]
  );

  const hasExpiring = useMemo(() => getStockMsg(product?.COD_PRODUTO), [getStockMsg, product?.COD_PRODUTO]);

  const expiringSeverity = useMemo<'danger' | 'warning' | 'normal'>(() => {
    const msg = hasExpiring || '';
    const match = msg.match(/(\d+)/);
    const days = match ? Number(match[1]) : NaN;

    if (!isNaN(days)) {
      if (days >= 0 && days <= 60) return 'danger';
      if (days <= 120 && days >= 61) return 'warning';
    }
    return 'normal';
  }, [hasExpiring]);

  const totalQuantity = useMemo(() => {
    if (!hasMultiplePacking) return 0;

    let count = 0;
    currentCartDetails?.items?.reduce((acc, currentValue) => {
      if (currentValue?.sku === product?.COD_PRODUTO) {
        const [, qtd] = currentValue?.option_uid?.split('_');
        const qty = Number(qtd);
        const val = currentValue?.quantity * qty;
        count = acc += val;
      }
      return acc;
    }, 0);
    return count;
  }, [currentCartDetails, hasMultiplePacking, product?.COD_PRODUTO, direction]);

  const rawCurrentAddedQty = useMemo(() => {
    const maximum = packing?.QTD_MULTIPLO_VENDA > 1 && pack === 'PK';

    if (hasMultiplePacking && hasMultiplePacking?.sku) {
      return totalQuantity;
    }

    if (!hasMultiplePacking?.sku && quantity) {
      return maximum ? packing?.QTD_MULTIPLO_VENDA * quantity : packing?.QTD_EMBALAGEM * quantity;
    }

    return 0;
  }, [hasMultiplePacking, pack, packing?.QTD_EMBALAGEM, packing?.QTD_MULTIPLO_VENDA, quantity, totalQuantity]);

  const rawQuantity = useMemo(() => {
    const limit = getLimit(product?.COD_PRODUTO);
    const synced = stockLeft?.[product?.COD_PRODUTO]?.stock_left;
    // Igual `Products` / `AddToCart`: só confia em saldo local quando é número > 0; senão usa limite ERP.
    // Evita marcar tudo indisponível quando existe registro truthy `{}` ou `stock_left` 0/ausente após troca de loja.
    const basisQty = typeof synced === 'number' && synced > 0 ? synced : limit;

    if (rawCurrentAddedQty) {
      return basisQty <= rawCurrentAddedQty ? 0 : basisQty - rawCurrentAddedQty;
    }
    return basisQty;
  }, [getLimit, product?.COD_PRODUTO, stockLeft, rawCurrentAddedQty]);

  const packRequiredQty = useMemo(() => {
    const multiple = packing?.QTD_MULTIPLO_VENDA > 1 && pack === 'PK';
    return multiple ? packing?.QTD_MULTIPLO_VENDA : packing?.QTD_EMBALAGEM;
  }, [pack, packing?.QTD_EMBALAGEM, packing?.QTD_MULTIPLO_VENDA]);

  const hasStock = useMemo(() => {
    if (!packRequiredQty) return false;
    if (rawQuantity == null) return false;

    if (rawQuantity >= packRequiredQty) return true;

    if (rawCurrentAddedQty > 0) return true;

    return false;
  }, [rawQuantity, packRequiredQty, rawCurrentAddedQty]);

  const showInfosFlex = useMemo(() => {
    if (!product.EMBALAGEM) return false;

    const embalagem = product.EMBALAGEM.find((p) => {
      if (pack === 'PK') {
        return p.SG_EMBALAGEM === pack && packQuantity === p.QTD_MULTIPLO_VENDA;
      }
      return p.SG_EMBALAGEM === pack && packQuantity === p.QTD_EMBALAGEM;
    });

    if (!embalagem) return false;
    return embalagem.PERC_MAX_FLEX !== 0 && embalagem.PERC_MIN_FLEX !== 0;
  }, [pack, product.EMBALAGEM, packQuantity, direction]);


  const {
    effectiveRecord,
    effectiveFinalPrice,
    unitPrice: unityPrice,
  } = useMemo(() => {
    const result = getEffectivePriceRecordForPack({
      prices: productPrices,
      pack,
      packQty: packRequiredQty || 0,
      paymentCode: modePayment,
      selectedClient: selectedClientForPackPricing,
    });

    // Preço de combo só na tela de combos (`showComboPricing`) e no carrinho. Listagem, categoria,
    // busca e PDP sempre mostram o preço original — mesmo com o combo já ativo no carrinho.
    const displayComboPricing = Boolean(showComboPricing && comboCartPrice.hasComboDiscount);
    const comboFinalPrice = displayComboPricing ? comboCartPrice.comboUnitPrice : null;
    const final = discountPrice || comboFinalPrice || result.effectiveFinalPrice || 0;
    const unit = packRequiredQty && final ? Number((final / packRequiredQty).toFixed(2)) : result.unitPrice;

    return {
      effectiveRecord: result.effectiveRecord,
      effectiveFinalPrice: final,
      unitPrice: unit,
    };
  }, [
    productPrices,
    pack,
    packRequiredQty,
    modePayment,
    discountPrice,
    selectedClientForPackPricing,
    showComboPricing,
    comboCartPrice.hasComboDiscount,
    comboCartPrice.comboUnitPrice,
  ]);

  const hasComboDiscount = Boolean(showComboPricing && comboCartPrice.hasComboDiscount);

  // Tela de combo: ST recalculado no preço de combo. Card padrão: ST original de catálogo.
  const priceRecordForSt =
    hasComboDiscount && comboCartPrice.comboPriceRecord
      ? comboCartPrice.comboPriceRecord
      : price;

  const originalPackPrice = useMemo(() => {
    if (!hasComboDiscount || comboCartPrice.originalUnitPrice == null) return undefined;
    return comboCartPrice.originalUnitPrice;
  }, [hasComboDiscount, comboCartPrice.originalUnitPrice]);

  const originalUnitPrice = useMemo(() => {
    if (originalPackPrice == null || !packRequiredQty) return undefined;
    return Number((originalPackPrice / packRequiredQty).toFixed(2));
  }, [originalPackPrice, packRequiredQty]);

  const uid = useMemo(() => {
    const emb = getEmbalagemQty(packing);

    return `${product?.COD_PRODUTO}-${pack}-${emb}`;
  }, [product?.COD_PRODUTO, pack, packing, getEmbalagemQty]);

  const showSelo = useMemo(() => !!price?.VLR_FINAL && price?.TP_PROMO !== 'B', [price]);

  const showOldPrice = useMemo(() => price?.TP_PROMO !== 'B' && price?.VLR_PRECO > price.VLR_FINAL, [price]);

  const lastPurchase = useMemo(() => {
    if (!product?.ultimaCompra) return null;
    if (product?.ultimaCompra?.SEQPESSOA !== selectedClient?.COD_CLIENTE) return null;

    return {
      date: new Date(product?.ultimaCompra?.DT_COMPRA).toLocaleDateString('pt-BR'),
      quantity: product?.ultimaCompra?.QTDATENDIDA,
      value: product?.ultimaCompra?.VALOR,
    };
  }, [product, selectedClient?.COD_CLIENTE]);

  const handleOpenAttributesModal = () => {
    openModalAttributes({ product, pack, packQuantity, price })
  };

  const handleOpenFlexModal = useCallback(() => {
    openModalFlex({
      product,
      pack,
      packQuantity,
      price,
      substTrib: substTrib || [],
      hasStock,
      rawQuantity,
      uid: flexUid,
      discountScopeKey,
    });
  }, [openModalFlex, product, pack, packQuantity, price, substTrib, hasStock, rawQuantity, flexUid, discountScopeKey]);

  useEffect(() => {
    isChangedPack.current = false;
  }, [product?.COD_PRODUTO, selectedClientScope?.COD_LOJA, product?.EMBALAGEM?.length]);

  useEffect(() => {
    const hasEmb = product && product.EMBALAGEM && product.EMBALAGEM.length > 0;

    if (hasEmb && !pack && !packQuantity) {
      setPackage(product.EMBALAGEM[0]?.SG_EMBALAGEM);
      setPackQuantity(getEmbalagemQty(product.EMBALAGEM[0]));
    }
  }, [product, pack, packQuantity, getEmbalagemQty]);

  // auto-ajuste de embalagem quando não tem preço efetivo ou não tem estoque
  useEffect(() => {
    if (isChangedPack.current) return;

    const hasEmb = product?.EMBALAGEM?.length > 0;
    if (!hasEmb) return;

    const currentQty = pack === 'PK' ? packing?.QTD_MULTIPLO_VENDA || 0 : packing?.QTD_EMBALAGEM || 0;
    const hasEffectivePrice = Boolean(discountPrice || price?.VLR_FINAL || effectiveFinalPrice);
    const canKeepCurrent = Boolean(pack && packQuantity && hasStock && hasEffectivePrice && currentQty > 0);

    const syncStock = stockLeft?.[product?.COD_PRODUTO]?.stock_left;
    const totalStock =
      (typeof syncStock === 'number' && syncStock > 0 ? syncStock : getLimit(product?.COD_PRODUTO)) || 0;
    // estoque restante considerando o que já está no carrinho para este produto
    const remainingUnits = Math.max(0, totalStock - (totalQuantity || 0));

    const findPriceRecordForEmb = (emb: PackagingProductProps) => {
      const qty = getEmbalagemQty(emb);
      const rec = productPrices.find((pr) => {
        if (emb.SG_EMBALAGEM === 'PK') {
          return pr.SG_EMBALAGEM === 'PK' && pr.QTD_MULTIPLO_VENDA === qty && pr.NROCONDICAOPAGTO === modePayment;
        }
        return pr.SG_EMBALAGEM === emb.SG_EMBALAGEM && pr.QTD_EMBALAGEM === qty && pr.NROCONDICAOPAGTO === modePayment;
      });
      return rec;
    };

    // Ordenar por maior quantidade primeiro
    const orderedByQtyDesc = [...product.EMBALAGEM].sort((a, b) => {
      const aQty = (a.QTD_MULTIPLO_VENDA || 1) * (a.QTD_EMBALAGEM || 1);
      const bQty = (b.QTD_MULTIPLO_VENDA || 1) * (b.QTD_EMBALAGEM || 1);
      return bQty - aQty;
    });

    const withPriceAndStock = orderedByQtyDesc.find((emb) => {
      const qty = getEmbalagemQty(emb);
      const rec = findPriceRecordForEmb(emb);
      const hasAnyPrice = Boolean(rec?.VLR_FINAL || rec?.VLR_PRECO);
      return hasAnyPrice && remainingUnits >= (qty || 0);
    });

    // Se o pack atual é válido mas existe um com maior quantidade (e com preço + estoque), prioriza o maior
    if (canKeepCurrent && withPriceAndStock) {
      const bestQty = getEmbalagemQty(withPriceAndStock);
      if ((bestQty || 0) > (currentQty || 0)) {
        setPackage(withPriceAndStock.SG_EMBALAGEM as PackagesTypesProps);
        setPackQuantity(bestQty || 0);
        isChangedPack.current = true;
      }
      return;
    }

    // Se encontrou uma embalagem disponível, seleciona; senão mantém a atual (evita selecionar indisponível)
    if (withPriceAndStock) {
      const qty = getEmbalagemQty(withPriceAndStock);
      setPackage(withPriceAndStock.SG_EMBALAGEM as PackagesTypesProps);
      setPackQuantity(qty || 0);
      isChangedPack.current = true;
    }
  }, [
    product,
    productPrices,
    modePayment,
    discountPrice,
    price?.VLR_FINAL,
    effectiveFinalPrice,
    hasStock,
    getLimit,
    totalQuantity,
    stockLeft,
    pack,
    packQuantity,
    packing?.QTD_EMBALAGEM,
    packing?.QTD_MULTIPLO_VENDA,
    getEmbalagemQty,
  ]);

  return (
    <div style={{ position: 'relative', height: '100%' }} id={`product-${product.COD_PRODUTO}`}>
      <S.ProductCard $showSelo={showSelo && hasStock}>
        <S.ProductContent direction={direction}>
          <S.Container>
            <S.ContentWrapper>
              <S.InfoRow>
                <S.EanWrapper $isList={direction === 'list'}>
                  {sku && <S.ProductCode
                    onClick={() => {
                      if (sku) {
                        copyToClipboard({
                          value: sku.toString(),
                          title: `Cód. <b>${sku}</b> copiado com sucesso`,
                        });
                      }
                    }}
                    $showSelo={showSelo && hasStock}
                  >
                    {sku}
                  </S.ProductCode>}
                  {ean && <S.ProductCode
                    $showSelo={showSelo && hasStock}
                    onClick={() => {
                      if (ean) {
                        copyToClipboard({
                          value: ean,
                          title: `EAN <b>${ean}</b> copiado com sucesso`,
                        });
                      }
                    }}
                  >
                    {ean}
                  </S.ProductCode>}
                </S.EanWrapper>
                <S.CommissionFlag $showSelo={showSelo && hasStock}>{commissionFlag}</S.CommissionFlag>
              </S.InfoRow>

              <S.ContentRow $isList={direction === 'list'}>
                <S.PaddingImage direction={direction}>
                  <S.ContentImage to={`/${product.COD_PRODUTO}`} onClick={onProductNavigate}>
                    <ProductImage COD_PRODUTO={product.COD_PRODUTO} />
                  </S.ContentImage>
                </S.PaddingImage>

                <S.Div>
                  <ProductInfo
                    product={product}
                    ean={ean}
                    commissionFlag={commissionFlag}
                    cardDescription={cardDescription}
                    onProductNavigate={onProductNavigate}
                  />

                  {showInfos && (
                    <S.Row>
                      <S.AddList
                        data-test={`open-modal-custom-attributes-${product.COD_PRODUTO}`}
                        onClick={handleOpenAttributesModal}
                      >
                        <S.BtnAddList $grey={1} $hasExpiring={!!hasExpiring} $expiringStatus={expiringSeverity}>
                          <RiInformationLine
                            size={20}
                            color={
                              hasExpiring && expiringSeverity === 'warning'
                                ? colors.alert.orange300
                                : hasExpiring && expiringSeverity === 'danger'
                                  ? colors.alert.red200
                                  : colors.brand.grafite
                            }
                          />
                        </S.BtnAddList>

                        {hasExpiring && (
                          <S.ExpiringText $severity={expiringSeverity}>
                            <RiCalendarTodoLine size={16} />
                            {getStockMsg(product?.COD_PRODUTO)}
                          </S.ExpiringText>
                        )}
                      </S.AddList>

                      <ComboProduct COD_PRODUTO={product.COD_PRODUTO} direction={direction} />
                    </S.Row>
                  )}
                </S.Div>
              </S.ContentRow>

              <S.CustomDiv>
                {/* AQUI: fullBorder também quando NÃO tiver estoque */}
                <Packaging
                  showQtd
                  pack={pack}
                  packQty={packQuantity}
                  product={product}
                  canDisablePack={false}
                  setPackage={setPackageIntercept}
                  setPackQuantity={setPackQuantityIntercept}
                  fullBorder={!effectiveFinalPrice || !hasStock}
                />

                {effectiveFinalPrice > 0 && hasStock && (
                  <PriceContent
                    discountPrice={discountPrice || 0}
                    selectedClient={selectedClientScope}
                    uid={uid}
                    discounts={discounts}
                    showFlex={showFlex && !applyComboPricing}
                    showInfosFlex={showInfosFlex}
                    handleOpenFlexModal={handleOpenFlexModal}
                    product={product}
                    showOldPrice={showOldPrice}
                    hasStock={hasStock}
                    price={price}
                    unityPrice={unityPrice}
                    effectiveFinalPrice={effectiveFinalPrice}
                    packQty={packRequiredQty || 0}
                    effectiveRecord={effectiveRecord}
                    rawQuantity={rawQuantity}
                    packQuantity={packQuantity}
                    direction={direction}
                    hasComboDiscount={hasComboDiscount}
                    originalUnitPrice={originalUnitPrice}
                    originalPackPrice={originalPackPrice}
                    priceRecordForSt={priceRecordForSt}
                    flexState={flexState}
                    flexPercentual={flexPercentual}
                  />
                )}

                <LastPurchase lastPurchase={lastPurchase} direction={direction} label="itens" />

                <AddToCart
                  pack={pack}
                  label="Adicionar"
                  product={product}
                  background="light"
                  packQty={packQuantity}
                  id={`${product.COD_PRODUTO}`}
                  unavailabel={!(effectiveFinalPrice > 0) || !hasStock}
                />
              </S.CustomDiv>
            </S.ContentWrapper>
          </S.Container>
        </S.ProductContent>
      </S.ProductCard>
    </div>
  );
};

export default memo(RenderProducts);
